import { excelDownloadId, pdfDownloadId, searchId } from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import { FC, ReactElement, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  DataHeader,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  Grid,
  Input,
  Radio,
  Select,
  Typography,
} from "@payping/web-components";
import { SDKDateObject, dateFormats, toUtc } from "@payping/utils/dateUtils";
import { CuToman, PhRDownloadSimple } from "@payping/icons";
import { useAsync } from "react-use";
import { getClients, getIpgList } from "@payping/redux/actions";
import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { PaymentStatusEnum, PaymentTypeEnum, sdkFetch, uploadV2Apis } from "@payping/api";
import { DownloadTypes } from "./AppSubNav/Download";
import { transactionFiltersSearchVariant, TransactionsFilters } from "@/pages/Sale/Report/ShowAllTransactionDetails";

interface Props {
  retry: () => void;
  loading: boolean;
  filters: TransactionsFilters;
  onChangeFilters: (filters: TransactionsFilters) => void;
}

interface FormInputs {
  rangePicker?: SDKDateObject[] | [undefined, undefined];
  amount?: number | null;
  paymentType?: PaymentTypeEnum | null;
  paymentStatus?: PaymentStatusEnum | null;
  clientId?: string | null;
  clientRefId?: string | null;
  code?: string | null;
  refId?: string | null;
  ipgRefId?: string | null;
  cardNumber?: string | null;
  payerName?: string | null;
  payerPhoneNumber?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  searchVariant?: (typeof transactionFiltersSearchVariant)[number] | null;
}

/**
 * TransactionFilter component.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.loading Whether the component is loading.
 * @param {TransactionsFilters} props.filters The filters of the transactions.
 * @param {(filters: TransactionsFilters) => void} props.onChangeFilters The callback function to change the filters.
 * @returns {ReactElement} The TransactionFilter component.
 */
export const TransactionFilter: FC<Props> = ({ loading, filters, onChangeFilters }) => {
  const location = useLocation();
  const { xl } = Grid.useBreakpoint();

  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [form] = Form.useForm<FormInputs>();
  const backFormat = dateFormats.standard.utcDateTime;

  const { loading: isLoadingClients, value: clients } = useAsync(() => dispatch(getClients()), []);
  const { loading: isLoadingIpgList, value: ipgList } = useAsync(() => dispatch(getIpgList()), []);

  useEffect(() => {
    form.setFieldsValue({
      clientId: filters.clientId ?? undefined,
      amount: filters.amount ?? undefined,
      paymentType: filters.paymentType ?? undefined,
      paymentStatus: filters.paymentStatus ?? PaymentStatusEnum.IsPaid,
      clientRefId: filters.clientRefId ?? undefined,
      code: filters.code ?? undefined,
      ipgRefId: filters.ipgRefId ?? undefined,
      refId: filters.refId ?? undefined,
      cardNumber: filters.cardNumber ?? undefined,
      payerName: filters.payerName ?? undefined,
      payerPhoneNumber: filters.payerPhoneNumber ?? undefined,
      rangePicker: [
        filters.fromDate
          ? toUtc({
              convertTime: false,
              date: filters.fromDate,
              dateFormat: dateFormats.standard.utcDateTime,
            }).object
          : undefined,
        filters.toDate
          ? toUtc({
              convertTime: false,
              date: filters.toDate,
              dateFormat: dateFormats.standard.utcDateTime,
            }).object
          : undefined,
      ],
      searchVariant: filters.searchVariant ?? "searchByType",
    });
  }, [filters]);

  const onFinish = (values: FormInputs) => {
    const [from, to] = values.rangePicker
      ? [values.rangePicker[0]?.format(backFormat), values.rangePicker[1]?.format(backFormat)]
      : [undefined, undefined];
    onChangeFilters({
      amount: values.amount,
      cardNumber: values.cardNumber,
      clientRefId: values.clientRefId,
      code: values.code,
      ipgRefId: values.ipgRefId,
      payerName: values.payerName,
      payerPhoneNumber: values.payerPhoneNumber,
      refId: values.refId,
      clientId: values.clientId,
      fromDate: from,
      paymentStatus: values.paymentStatus,
      paymentType: values.paymentType,
      toDate: to,
      searchVariant: values.searchVariant,
    });
    setOpenDrawer(false);
  };

  const paymentStatusList = [
    { label: t("report.filters.paymentStatus.all"), value: PaymentStatusEnum.Default },
    { label: t("report.filters.paymentStatus.paid"), value: PaymentStatusEnum.IsPaid },
    { label: t("report.filters.paymentStatus.notPaid"), value: PaymentStatusEnum.UnPaid },
  ];

  const handleDownload = useCallback(
    async (type: DownloadTypes, newWindow: Window | null): Promise<void> => {
      const body = {
        amount: filters.amount ?? undefined,
        cardNumber: filters.cardNumber ?? undefined,
        clientId: filters.clientId ?? undefined,
        clientRefId: filters.clientRefId,
        code: filters.code ?? undefined,
        fromDate: filters.fromDate ?? undefined,
        ipgRefId: filters.ipgRefId ?? undefined,
        payerName: filters.payerName,
        payerPhoneNumber: filters.payerPhoneNumber ?? undefined,
        paymentStatus: filters.paymentStatus ?? undefined,
        paymentType: filters.paymentType ?? undefined,
        refId: filters.refId ?? undefined,
        toDate: filters.toDate ?? undefined,
      };
      let url;
      if (type === "Excel") {
        const response = await sdkFetch("/v2/upload/PaymentsExcelExport", {
          body: JSON.stringify(body),
          method: "POST",
        });
        url = await response.text();
      } else {
        // @ts-expect-error paymentType
        url = await uploadV2Apis.paymentsPdfExportWithModelPost(body);
      }
      newWindow?.location?.assign?.(url);
    },
    [filters],
  );

  const items = [
    {
      key: "1",
      label: <span data-google-track={excelDownloadId}>{t("downloadExcel")}</span>,
      onClick: () => {
        const newWindow = window.open(undefined, "_blank");
        handleDownload("Excel", newWindow);
      },
    },
    {
      key: "2",
      label: <span data-google-track={pdfDownloadId}>{t("downloadPdf")}</span>,
      onClick: () => {
        const newWindow = window.open(undefined, "_blank");
        handleDownload("PDF", newWindow);
      },
    },
  ];

  return (
    <Form<FormInputs>
      onFinish={onFinish}
      form={form}
      onValuesChange={async (changedValues) => {
        if ("searchVariant" in changedValues) {
          form.resetFields(["payerName", "payerPhoneNumber", "code", "refId", "clientRefId", "ipgRefId", "cardNumber"]);
        }
      }}
    >
      <DataHeader
        title={
          <Typography.Title className={"!tw-m-0"} level={4}>
            {t("mySale.tabs.report.transactions.recent")}
          </Typography.Title>
        }
        filters={{
          searchbarInput: xl ? (
            <>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
                key={1}
              >
                {(form1): ReactElement => (
                  <Form.Item name={form1.getFieldValue("searchVariant")} noStyle>
                    <Input.Search
                      disabled={form1.getFieldValue("searchVariant") === "searchByType"}
                      allowClear
                      onSearch={() => form.submit()}
                      addonBefore={
                        <Form.Item name={"searchVariant"} noStyle>
                          <Select dropdownMatchSelectWidth={false}>
                            <Select.Option disabled={true} value={"searchByType"}>
                              {t("report.searchBy")}
                            </Select.Option>
                            <Select.Option value={"payerName"}>{t("report.filters.payerName")}</Select.Option>
                            <Select.Option value={"payerPhoneNumber"}>
                              {t("report.filters.payerPhoneNumber")}
                            </Select.Option>
                            <Select.Option value={"code"}>{t("report.filters.code")}</Select.Option>
                            <Select.Option value={"refId"}>{t("report.filters.refId")}</Select.Option>
                            <Select.Option value={"clientRefId"}>{t("report.filters.clientRefId")}</Select.Option>
                            <Select.Option value={"ipgRefId"}>{t("report.filters.ipgRefId")}</Select.Option>
                            <Select.Option value={"cardNumber"}>{t("report.filters.cardNumber")}</Select.Option>
                          </Select>
                        </Form.Item>
                      }
                      placeholder={t("report.search")}
                      data-google-track={searchId}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </>
          ) : null,
          datepickerInput: (
            <Form.Item className={"tw-m-0"} name="rangePicker">
              <DatePicker.RangePicker onChange={() => form.submit()} className={"w-100"} allowClear />
            </Form.Item>
          ),
          filtersButton: (
            <>
              {/* < Filters Button & Badge > */}
              <FiltersButton
                onClick={() => setOpenDrawer(true)}
                params={{
                  amount: filters.amount ?? undefined,
                  clientId: filters.clientId ?? undefined,
                  paymentStatus: filters.paymentStatus ?? undefined,
                  paymentType: filters.paymentType ?? undefined,
                  searchVariant: filters.searchVariant ?? undefined,
                }}
              />
              {/* </ Filters Button & Badge > */}

              {/* < FiltersDrawer > */}
              <FiltersDrawer
                title={t("filter")}
                onClose={() => setOpenDrawer(false)}
                open={openDrawer}
                loading={loading}
                onClickApplyFilters={() => {
                  form.submit();
                  navigate({ pathname: location.pathname });
                  setOpenDrawer(false);
                }}
                onClickResetFilters={() => {
                  navigate({ pathname: location.pathname });
                  form.resetFields();
                  setOpenDrawer(false);
                }}
              >
                <Flex vertical gap={8}>
                  {/* < Amount > */}
                  <Form.Item label={t("report.filters.amount")} name="amount" layout={"vertical"}>
                    <Input.Number
                      className="tw-w-full"
                      suffix={<CuToman width={24} height={24} />}
                      separateThousandth
                      inputMode="numeric"
                      changeOnWheel={false}
                      controls={false}
                    />
                  </Form.Item>
                  {/* < Amount /> */}

                  {!xl ? (
                    <>
                      {/* < SearchVariant > */}
                      <Form.Item
                        label={t("report.search")}
                        shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
                        key={1}
                        layout={"vertical"}
                      >
                        {(form1): ReactElement => (
                          <Form.Item name={form1.getFieldValue("searchVariant")} noStyle>
                            <Input
                              disabled={form1.getFieldValue("searchVariant") === "searchByType"}
                              allowClear
                              addonBefore={
                                <Form.Item name={"searchVariant"} noStyle>
                                  <Select dropdownMatchSelectWidth={false}>
                                    <Select.Option disabled={true} value={"searchByType"}>
                                      {t("report.searchBy")}
                                    </Select.Option>
                                    <Select.Option value={"payerName"}>{t("report.filters.payerName")}</Select.Option>
                                    <Select.Option value={"payerPhoneNumber"}>
                                      {t("report.filters.payerPhoneNumber")}
                                    </Select.Option>
                                    <Select.Option value={"code"}>{t("report.filters.code")}</Select.Option>
                                    <Select.Option value={"refId"}>{t("report.filters.refId")}</Select.Option>
                                    <Select.Option value={"clientRefId"}>
                                      {t("report.filters.clientRefId")}
                                    </Select.Option>
                                    <Select.Option value={"ipgRefId"}>{t("report.filters.ipgRefId")}</Select.Option>
                                    <Select.Option value={"cardNumber"}>{t("report.filters.cardNumber")}</Select.Option>
                                  </Select>
                                </Form.Item>
                              }
                              placeholder={t("report.search")}
                              data-google-track={searchId}
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                      {/* </ SearchVariant > */}
                    </>
                  ) : null}

                  {/* < Noe Service > */}
                  <Form.Item label={t("report.filters.clientId")} name="clientId" layout={"vertical"}>
                    <Select allowClear disabled={isLoadingClients}>
                      {clients?.map(({ displayName, clientId: cId }) => (
                        <Select.Option key={cId} value={cId}>
                          {displayName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {/* </ Noe Service > */}

                  {/* < Noe Dargah > */}
                  <Form.Item label={t("report.filters.paymentType")} name="paymentType" layout={"vertical"}>
                    <Select allowClear disabled={isLoadingIpgList}>
                      {ipgList
                        ?.filter(({ name }) => !!name)
                        .map(({ id, name }) => (
                          <Select.Option key={id} value={id}>
                            {name as string}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                  {/* </ Noe Dargah > */}

                  {/* < Vaziat Tarakonesh > */}
                  <Form.Item label={t("report.filters.paymentStatus.title")} name="paymentStatus">
                    <Radio.Group options={paymentStatusList} />
                  </Form.Item>
                  {/* </ Vaziat Tarakonesh > */}
                </Flex>
              </FiltersDrawer>
              {/* </ FiltersDrawer > */}
            </>
          ),
          downloadButton: (
            <Form.Item noStyle key={3}>
              <Dropdown
                menu={{
                  items,
                }}
              >
                <Button block icon={<PhRDownloadSimple width={14} height={14} />}>
                  {t("downloadFile")}
                </Button>
              </Dropdown>
            </Form.Item>
          ),
        }}
      />
    </Form>
  );
};
